import React, { useState } from 'react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const QueueView = ({ queue, removeFromQueue, processQueue }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const totalCost = queue.reduce((acc, item) => {
    const suggestedOrder = item.maxStock - item.stock;
    return acc + (suggestedOrder * item.unitCost);
  }, 0);

  const handleProcess = () => {
    if (queue.length === 0) return;
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setShowSuccess(true);
      processQueue();
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1500);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Purchase Queue</h2>
          <p className="text-gray-400 mt-1">Review and approve suggested reorders.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card title="Pending Items">
            <div className="space-y-3">
              <AnimatePresence>
                {queue.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="py-12 text-center border-2 border-dashed border-sp-gray-700 rounded-lg bg-sp-gray-800/30"
                  >
                    <SafeIcon name="Inbox" className="text-4xl text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 font-mono text-sm">Queue is empty.</p>
                    <p className="text-gray-500 text-xs mt-1">Add items from Alerts to generate POs.</p>
                  </motion.div>
                ) : (
                  queue.map((item, idx) => {
                    const suggestedOrder = item.maxStock - item.stock;
                    const estimatedCost = suggestedOrder * item.unitCost;
                    
                    return (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        key={item.id} 
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-sp-gray-800 border border-sp-gray-700 rounded-lg gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-white">{item.sku}</span>
                            <Badge variant="cyan">{item.supplierId}</Badge>
                          </div>
                          <div className="text-xs text-gray-400">{item.name}</div>
                        </div>
                        
                        <div className="flex items-center gap-6 bg-sp-gray-900 p-2 rounded border border-sp-gray-700/50">
                          <div className="text-center">
                            <div className="text-[10px] text-gray-500 font-mono uppercase">Order Qty</div>
                            <div className="font-mono text-sp-lime">{suggestedOrder}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[10px] text-gray-500 font-mono uppercase">Est. Cost</div>
                            <div className="font-mono text-white">${estimatedCost.toLocaleString()}</div>
                          </div>
                        </div>

                        <button 
                          onClick={() => removeFromQueue(item.id)}
                          className="p-2 text-gray-500 hover:text-sp-alert hover:bg-sp-alert/10 rounded transition-colors"
                          title="Remove from queue"
                        >
                          <SafeIcon name="Trash2" />
                        </button>
                      </motion.div>
                    );
                  })
                )}
              </AnimatePresence>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card title="Order Summary" className="sticky top-24">
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Total Items</span>
                <span className="font-mono text-white">{queue.length}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Total Units</span>
                <span className="font-mono text-white">
                  {queue.reduce((acc, item) => acc + (item.maxStock - item.stock), 0)}
                </span>
              </div>
              <div className="h-px w-full bg-sp-gray-700 my-2"></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300 font-medium">Estimated Total</span>
                <span className="font-mono text-xl text-sp-lime shadow-[0_0_10px_rgba(204,255,0,0.1)]">
                  ${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>

              {showSuccess ? (
                 <div className="mt-6 p-4 bg-sp-lime/10 border border-sp-lime/30 rounded-lg text-center flex flex-col items-center gap-2 text-sp-lime">
                    <SafeIcon name="CheckCircle" className="text-2xl" />
                    <span className="font-mono text-sm">POs Generated Successfully</span>
                 </div>
              ) : (
                <button 
                  disabled={queue.length === 0 || isProcessing}
                  onClick={handleProcess}
                  className={`mt-6 w-full py-3 rounded-md font-mono font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2
                    ${queue.length === 0 
                      ? 'bg-sp-gray-800 text-gray-600 cursor-not-allowed' 
                      : 'bg-sp-lime text-sp-black hover:bg-white shadow-neon hover:shadow-neon-strong'}`}
                >
                  {isProcessing ? (
                    <>
                      <SafeIcon name="Loader" className="animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <SafeIcon name="Send" /> Approve & Generate POs
                    </>
                  )}
                </button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </motion.div>
  );
};

export default QueueView;